// The React module registry: every modules.json `renderer` name → its React module
// (component-registry.ts — the shell renders through it since 05j-1). Called once after
// the legacy scripts load (App.tsx). (The legacy renderer files were deleted in 05k-2.)

import { registerReactModule } from "./bridge.js";
import { LeagueTableModule } from "./LeagueTableModule.js";
import { MatchResultsModule } from "./MatchResultsModule.js";
import { SquadModule } from "./SquadModule.js";
import { BioModule } from "./BioModule.js";
import { MatchModule } from "./MatchModule.js";
import { MapPanelModule } from "./MapPanelModule.js";
import { NotesModule } from "./NotesModule.js";
import { RankingsModule } from "./RankingsModule.js";
import { FilterModule } from "./FilterModule.js";
import { StatsTableModule } from "./StatsTableModule.js";
import { StatsModule } from "./StatsModule.js";
import { RadarModule } from "./RadarModule.js";
import { ComparisonModule } from "./ComparisonModule.js";
import { SimilarityModule } from "./SimilarityModule.js";
import { MatchesModule } from "./MatchesModule.js";
import { HeatMapModule } from "./HeatMapModule.js";
import { ShotMapModule } from "./ShotMapModule.js";
import { PassMapModule } from "./PassMapModule.js";
import { PassingProfileModule } from "./PassingProfileModule.js";
import { PassingReportModule } from "./PassingReportModule.js";
import { ZoneFinderModule } from "./ZoneFinderModule.js";
import { SNAPSHOT_BUILD } from "../live/flag.js";

// These seed React state from their `_`-state once, so every refresh rebuilds them — as
// the legacy renderers rebuilt their innerHTML on every call (a chip's ✕, the overlay, a
// player change, the top bar's Refresh). Restores no longer need it (05i-1). See
// RegisterOptions.rebuildOnRefresh in bridge.tsx (04-fix5, 05i fork 2A).
const REBUILD = { rebuildOnRefresh: true };

export function registerReactModules(): void {
  registerReactModule("renderLeagueTableModule", LeagueTableModule, REBUILD);
  registerReactModule("renderMatchResultsModule", MatchResultsModule, REBUILD);
  registerReactModule("renderSquadsModule", SquadModule, REBUILD);
  registerReactModule("renderBioModule", BioModule, { livePlayers: true });
  registerReactModule("renderMatchModule", MatchModule);
  registerReactModule("renderMapPanelModule", MapPanelModule);
  registerReactModule("renderNotesModule", NotesModule);
  registerReactModule("renderRankingModule", RankingsModule, REBUILD);
  registerReactModule("renderFilterModule", FilterModule, REBUILD);
  registerReactModule("renderStatsTableModule", StatsTableModule, REBUILD);
  registerReactModule("renderStatsModule", StatsModule, { ...REBUILD, livePlayers: true });
  registerReactModule("renderRadarModule", RadarModule, { livePlayers: true });
  registerReactModule("renderPlayerComparisonModule", ComparisonModule, REBUILD);
  registerReactModule("renderSimilarityModule", SimilarityModule, { ...REBUILD, livePlayers: true });
  registerReactModule("renderMatchesModule", MatchesModule, { livePlayers: true });
  registerReactModule("renderHeatMapModule", HeatMapModule, { livePlayers: true });
  registerReactModule("renderShotMapModule", ShotMapModule, { livePlayers: true });
  registerReactModule("renderPassMapModule", PassMapModule, { livePlayers: true });
  registerReactModule("renderPassingProfileModule", PassingProfileModule, { livePlayers: true });
  registerReactModule("renderPassingReportModule", PassingReportModule, { livePlayers: true });
  // Reads its `_`-state every render — and must NOT rebuild on refresh: the empty-message
  // quirk it keeps depends on surviving refreshes, as the legacy's persistent DOM does.
  // Not in the clean build (step 1b): its data is the snapshot's, and leaving it out lets the
  // bundler drop the snapshot code with it. (Its tile is hidden whenever LIVE.)
  if (SNAPSHOT_BUILD) registerReactModule("renderZoneFinderModule", ZoneFinderModule);
}
